"use client";

import type { api } from "@v1/backend/convex/_generated/api";
import type { FunctionReturnType } from "convex/server";
import { createContext, type ReactElement, type ReactNode, useContext } from "react";
import type { MessengerSurface } from "./messenger-types";
import type { MessengerNav } from "./use-messenger-nav";

export type MessengerConfigOn = Extract<
  FunctionReturnType<typeof api.customerChat.messenger_lists.getMessengerConfig>,
  { state: "on" }
>;

export type MessengerUnreadItem = FunctionReturnType<
  typeof api.customerChat.messenger_lists.getMyUnread
>["items"][number];

/** Signed-in person (portal tenant, app staff) or anonymous help-center visitor. */
export type MessengerAudience =
  | { readonly kind: "member"; readonly surface: MessengerSurface }
  | { readonly kind: "visitor"; readonly orgSlug: string; readonly orgName: string };

export interface MessengerContextValue {
  readonly audience: MessengerAudience;
  readonly config: MessengerConfigOn;
  readonly nav: MessengerNav;
  readonly unread: readonly MessengerUnreadItem[];
  /** Null on the full-page variant (portal mobile), which has no close. */
  readonly close: (() => void) | null;
}

const MessengerContext = createContext<MessengerContextValue | null>(null);

export function MessengerProvider({
  value,
  children,
}: {
  readonly value: MessengerContextValue;
  readonly children: ReactNode;
}): ReactElement {
  return <MessengerContext.Provider value={value}>{children}</MessengerContext.Provider>;
}

export function useMessenger(): MessengerContextValue {
  const value = useContext(MessengerContext);
  if (value === null) throw new Error("useMessenger outside MessengerProvider");
  return value;
}

/** Surface of a signed-in person; chats, tickets and unread exist only for them. */
export function useMessengerSurface(): MessengerSurface {
  const { audience } = useMessenger();
  if (audience.kind !== "member") throw new Error("member-only messenger view rendered for a visitor");
  return audience.surface;
}

export function isChatUnread(unread: readonly MessengerUnreadItem[], sessionId: string): boolean {
  return unread.some((item) => item.kind === "chat" && item.session_id === sessionId);
}

export function isTicketUnread(unread: readonly MessengerUnreadItem[], taskId: string): boolean {
  return unread.some((item) => item.kind === "ticket" && item.task_id === taskId);
}
